import React, { useState, Fragment, useEffect, useMemo } from "react";
import { gql, useMutation, useQuery } from "urql";
import { useParams, useLocation, Link } from "wouter";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Alert, AlertVariant } from "../../../../components/src/Alert";
import { cloneDeep, keyBy } from "lodash";

import { useAlertTimeOut, usePrevious } from "../../../../components/src/common/Utils";
import { Grid, Column } from "../../../../components/src";
import {
    NonAutoFillTextField,
    TextField,
    MultipleSelectField,
    TextAreaField,
    IntegerField,
    SubmitButton,
    SelectField,
    SearchMultipleSelectField,
    CheckBoxField
} from "../../../../components/src/form";
import { userHaveAccessTo, userIsSuperAdmin } from "../../common/Permissions";
import { PermissionAssetEnum, RoleTypeEnum, UserStatusEnum } from "../../types.generated";
import { formikUrqlErrorFormater } from "../../../../components/src/common/formik-urql-error-helper";

const ZERO_OBJECT_ID = "000000000000000000000000";

// IPv4: four octets 0-255 (e.g. 192.168.1.1)
const IPV4_REGEX =
    /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)$/;

// IPv6: full and compressed forms (e.g. 2001:db8::1, ::1)
const IPV6_REGEX =
    /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::(?:[0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,7}:$|^(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}$|^(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}$|^(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}$|^[0-9a-fA-F]{1,4}:(?::[0-9a-fA-F]{1,4}){1,6}$|^:(?::[0-9a-fA-F]{1,4}){1,7}$|^::$/;

function isValidIp(line: string): boolean {
    return IPV4_REGEX.test(line) || IPV6_REGEX.test(line);
}

function getIpWhitelistLines(value: string | string[] | undefined): string[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value.map((s) => String(s).trim()).filter(Boolean);
    return String(value)
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
}

const GET_ME = gql`
    query {
        me {
            clientIds
            clients {
                _id
                name
            }
            personalNumber
            roles {
                _id
                assets
                clientIds
                permissionType
                name
            }
        }
    }
`;

const GET_USER = gql`
    query user($id: GraphQLObjectId) {
        user(_id: $id) {
            _id
            clientIds
            clients {
                _id
                name
            }
            status
            name
            company
            frontendRoleId
            description
            bidExpiration
            personalNumber
            email
            phoneNumber
            roleIds
            noteIds
            notes {
                _id
                data
                createTimestamp
            }
            logins {
                timeStamp
                userAgent
            }
            clientId
            serviceUser
            ipWhitelist
            restApi
            graphqlApi
            updateUserId
            updateUserInfo {
                _id
                name
            }
            updateTimestamp
        }
    }
`;

const GET_ROLES_ETC = gql`
    query {
        roles {
            _id
            name
            assets
            updateTimestamp
            permissionType
            roleType
            clients {
                _id
                name
            }
        }

        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
        }

        integrators: parties(filter: { typeIn: [Integrator] }) {
            _id
            name
        }

        notes(filter: { typeIn: "TERMS_OF_SERVICE" }) {
            _id
            asset
            title
            tags {
                type
                value
            }
        }
    }
`;

const CREATE_USER = gql`
    mutation createUser($input: CreateUserInput!) {
        createUser(input: $input) {
            _id
        }
    }
`;

const UPDATE_USER = gql`
    mutation updateUser($input: UpdateUserInput!) {
        updateUser(input: $input) {
            _id
        }
    }
`;

const validationSchema = Yup.object({
    clientIds: Yup.array()
        .of(Yup.string().required("Each client Id is required")) // Optional: Validate each item
        .min(1, "At least one client Id is required")
        .required("Client Ids are required"),
    name: Yup.string().min(2, "Must be at least 2 characters").max(25, "Must be 25 characters or less").required("Required"),
    company: Yup.string().min(2, "Must be at least 2 characters").max(75, "Must be 75 characters or less").required("Required"),
    personalNumber: Yup.string()
        .min(2, "Must be at least 2 characters")
        .max(15, "Must be less than 15 characters")
        .required("Required")
        .matches(/^[a-z0-9]+$/, "Only digits and lowercase characters are allowed"),
    email: Yup.string().email("Invalid email address`").required("Required"),
    phoneNumber: Yup.string().max(20, "Must be 20 characters or less"),
    description: Yup.string().max(200, "Must be 200 characters or less"),
    password: Yup.string().required("Required").max(20, "Must be 20 characters or less"),
    confirmPassword: Yup.string().max(20, "Must be 20 characters or less"),
    ipWhitelist: Yup.string().test(
        "ip-whitelist-format",
        "Each line must be a valid IPv4 (e.g. 192.168.1.1) or IPv6 (e.g. 2001:db8::1) address.",
        (value) => {
            const lines = getIpWhitelistLines(value);
            return lines.every((line) => isValidIp(line));
        }
    )
});

const validationSchemaEditMode = Yup.object({
    clientIds: Yup.array()
        .of(Yup.string().required("Each client Id is required")) // Optional: Validate each item
        .min(1, "At least one client Id is required")
        .required("Client Ids are required"),
    name: Yup.string().min(2, "Must be at least 2 characters").max(25, "Must be 25 characters or less").required("Required"),
    company: Yup.string().min(2, "Must be at least 2 characters").max(75, "Must be 75 characters or less").required("Required"),
    personalNumber: Yup.string()
        .min(2, "Must be at least 2 characters")
        .max(15, "Must be less than 15 characters")
        .required("Required")
        .matches(/^[a-z0-9]+$/, "Only digits and lowercase characters are allowed"),
    email: Yup.string().email("Invalid email address`").required("Required"),
    phoneNumber: Yup.string().max(20, "Must be 20 characters or less"),
    description: Yup.string().max(200, "Must be 200 characters or less"),
    password: Yup.string().max(20, "Must be 20 characters or less"),
    confirmPassword: Yup.string().max(20, "Must be 20 characters or less"),
    ipWhitelist: Yup.string().test(
        "ip-whitelist-format",
        "Each line must be a valid IPv4 (e.g. 192.168.1.1) or IPv6 (e.g. 2001:db8::1) address.",
        (value) => {
            const lines = getIpWhitelistLines(value);
            return lines.every((line) => isValidIp(line));
        }
    )
});

export function AdminUserPage(): React.ReactElement {
    const { id }: any = useParams();
    const previousId = usePrevious(id);

    const isEditMode = !!id;
    const isCreateMode = !isEditMode;

    const [{ fetching: loadingMe, error: errorMe, data: getMe }] = useQuery({ query: GET_ME });
    const [{ fetching: loading, error, data }] = useQuery({ query: GET_USER, variables: { id }, pause: isCreateMode });
    const [{ fetching: loadingRoles, error: errorRoles, data: getRolesEtc }] = useQuery({
        query: GET_ROLES_ETC,
        requestPolicy: "network-only"
    });
    const [_, createUser] = useMutation(CREATE_USER);
    const [__, updateUser] = useMutation(UPDATE_USER);

    const [location, navigate] = useLocation();
    const [table, setTable] = useState([]);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });
    const keys: string[] = ["client", "roleName", ...Object.keys(PermissionAssetEnum)];

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (id !== previousId) {
            setTable([]);
        } else if (getMe && data && data.user && getRolesEtc) {
            const rolesById = keyBy(getRolesEtc.roles, "_id");
            const table = [];
            data.user.roleIds.forEach((roleId) => {
                const role = cloneDeep(rolesById[roleId]);
                if (role.clients.length === 0) {
                    const row = { client: "All", roleName: role.name };
                    if (role.assets.length === 0) {
                        Object.keys(PermissionAssetEnum).map((asset) => {
                            if (asset !== "Admin") {
                                row[asset] = role.permissionType;
                            }
                        });
                    } else {
                        role.assets.forEach((asset) => {
                            row[asset] = role.permissionType;
                        });
                    }
                    table.push(row);
                } else {
                    role.clients.forEach((client) => {
                        if (client) {
                            const row = { client: client.name, roleName: role.name };
                            if (role.assets.length === 0) {
                                Object.keys(PermissionAssetEnum).map((asset) => {
                                    if (asset !== "Admin") {
                                        row[asset] = role.permissionType;
                                    }
                                });
                            } else {
                                role.assets.forEach((asset) => {
                                    row[asset] = role.permissionType;
                                });
                            }
                            table.push(row);
                        }
                    });
                }
            });
            setTable(table);
        }
    }, [id, previousId, getMe, data, getRolesEtc]);

    const clientOptions = useMemo(() => {
        const clientOptions: { key: string; value: string; text: string }[] = [];

        if (getRolesEtc && getMe) {
            if (userIsSuperAdmin(getMe.me)) {
                getRolesEtc.clients.forEach((client) => {
                    clientOptions.push({ key: client._id, value: client.name, text: client.name });
                });
            } else {
                getMe.me.clients.forEach((client) => {
                    clientOptions.push({ key: client._id, value: client.name, text: client.name });
                });
            }
        }

        return clientOptions;
    }, [getRolesEtc, getMe]);

    const integratorOptions = useMemo(() => {
        const options: { key: string; value: string; text: string }[] = [{ key: ZERO_OBJECT_ID, value: "None", text: "None" }];
        if (getRolesEtc && getRolesEtc.integrators) {
            getRolesEtc.integrators.forEach((integrator) => {
                options.push({ key: integrator._id, value: integrator.name, text: integrator.name });
            });
        }
        return options;
    }, [getRolesEtc]);

    const noteOptions = useMemo(() => {
        const noteOptions: { key: string; value: string; text: string }[] = [];

        if (getRolesEtc) {
            console.log(getRolesEtc);

            getRolesEtc.notes.forEach((note) => {
                noteOptions.push({ key: note._id, value: note.title, text: note.title });
            });
        }
        return noteOptions;
    }, [getRolesEtc]);

    if (loadingMe || loading || loadingRoles)
        return (
            <div>
                <div>Loading</div>
            </div>
        );

    if (errorMe) return <pre>{JSON.stringify(errorMe, null, 2)}</pre>;
    if (error) return <pre>{JSON.stringify(error, null, 2)}</pre>;
    if (errorRoles) return <pre>{JSON.stringify(errorRoles, null, 2)}</pre>;

    if (!userHaveAccessTo("Any", PermissionAssetEnum.Admin, getMe.me.roles)) {
        return <div />;
    }

    const { roles } = getRolesEtc;

    // default user
    let user: any = {
        company: "Untitled",
        status: UserStatusEnum.Confirmed,
        clientIds: [],
        clientId: ZERO_OBJECT_ID,
        serviceUser: false,
        ipWhitelist: "",
        name: "",
        personalNumber: "",
        frontendRoleId: "000000000000000000000000",
        email: "",
        phoneNumber: "+46",
        bidExpiration: 0,
        roleIds: [],
        noteIds: [],
        description: "",
        password: "",
        restApi: false,
        graphqlApi: true
    };

    // edit mode
    if (isEditMode) {
        user = cloneDeep(data.user);
        user.confirmpassword = "";
        user.clientId = user.clientId ?? ZERO_OBJECT_ID;
        user.serviceUser = user.serviceUser ?? false;
        user.ipWhitelist = Array.isArray(user.ipWhitelist) ? user.ipWhitelist.join("\n") : (user.ipWhitelist ?? "");
        user.restApi = user.restApi ?? false;
        user.graphqlApi = user.graphqlApi ?? true;
    }

    const pageTitle = isCreateMode ? "New user" : "Edit user";
    const submitButtonLabel = isCreateMode ? "Create" : "Update";

    const frontendRoles = roles.filter((role) => {
        if (role.roleType === RoleTypeEnum.Frontend) {
            return true;
        } else {
            return false;
        }
    });
    let frontendRoleOptions = frontendRoles.map((role) => ({ key: role._id, value: role.name }));
    frontendRoleOptions = [{ key: "000000000000000000000000", value: "Select One" }, ...frontendRoleOptions];

    const backendRoles = roles.filter((role) => {
        if (role.roleType === RoleTypeEnum.Backend) {
            return true;
        } else {
            return false;
        }
    });

    const backendRoleOptions = backendRoles.map((role) => ({ key: role._id, value: role.name }));

    //Latest one that has been signed
    let signedTermsOfService = null;
    if (user && user.notes) {
        const sortedTermsOfService = user.notes.sort(function (n1, n2) {
            return new Date(n2.createTimestamp).getTime() - new Date(n1.createTimestamp).getTime();
        });
        signedTermsOfService = sortedTermsOfService[0];
    }

    const logins = data && data.user && data.user.logins ? data.user.logins.sort((a, b) => (a.timeStamp > b.timeStamp ? -1 : 0)) : [];

    return (
        <div className="admin-page max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1>{pageTitle}</h1>
            <div className="flex flex-wrap -mx-2">
                <Formik
                    enableReinitialize={true}
                    initialValues={user}
                    validationSchema={isEditMode ? validationSchemaEditMode : validationSchema}
                    onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                        const input = cloneDeep(submitValues);
                        delete input.clients;
                        delete input.confirmpassword;
                        delete input.__typename;
                        delete input.logins;
                        delete input.updateTimestamp;
                        delete input.updateUserInfo;
                        delete input.updateUserId;
                        delete input.notes;
                        input.restApi = !!input.restApi;
                        input.graphqlApi = !!input.graphqlApi;
                        input.serviceUser = !!input.serviceUser;
                        input.clientId = input.clientId || ZERO_OBJECT_ID;
                        const ipLines = getIpWhitelistLines(input.ipWhitelist);
                        if (ipLines.length > 0) {
                            input.ipWhitelist = ipLines;
                        } else {
                            delete input.ipWhitelist;
                        }
                        if (isCreateMode) {
                            let newUserId: any;
                            await createUser({ input })
                                .then((result) => {
                                    if ("error" in result && result.error) {
                                        const message = formikUrqlErrorFormater(result.error, setErrors);
                                        setAlert({ color: "danger", visible: true, message });
                                    } else {
                                        //resetForm(user);
                                        newUserId = result.data.createUser._id;
                                        setAlert({
                                            color: "success",
                                            visible: true,
                                            message: `New user '${input.personalNumber}' created successfully!`
                                        });
                                    }
                                })
                                .catch((error) => {
                                    setAlert({ color: "danger", visible: true, message: error.toString() });
                                })
                                .finally(() => {
                                    setSubmitting(false);
                                    // redirect to edit user page on success
                                    if (newUserId) {
                                        const pathname = location.split("?")[0].split("#")[0];
                                        const path = pathname.split("/");
                                        path.pop();
                                        path.push(newUserId);
                                        navigate(path.join("/"), { replace: true });
                                    }
                                });
                        } else {
                            await updateUser({ input })
                                .then((result) => {
                                    if ("error" in result && result.error) {
                                        const message = formikUrqlErrorFormater(result.error, setErrors);
                                        setAlert({ color: "danger", visible: true, message });
                                    } else {
                                        setAlert({
                                            color: "success",
                                            visible: true,
                                            message: `The user '${input.personalNumber}' updated successfully!`
                                        });
                                    }
                                })
                                .catch((error) => {
                                    setAlert({ color: "danger", visible: true, message: error.toString() });
                                })
                                .finally(() => {
                                    setSubmitting(false);
                                });
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Fragment>
                            <div className="w-full px-2">
                                <Form autoComplete="off">
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-1/3 px-2 mb-4">
                                            <SearchMultipleSelectField
                                                name="clientIds"
                                                label="Clients. This user belong to these Clients"
                                                className=""
                                                disabled={isSubmitting}
                                                options={clientOptions}
                                            />
                                            <TextField name="name" label="Name" type="text" className="" disabled={isSubmitting} />
                                            <SelectField
                                                name="status"
                                                label="Status"
                                                options={Object.keys(UserStatusEnum)}
                                                className=""
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="personalNumber"
                                                label="Personal number"
                                                type="text"
                                                className=""
                                                autoComplete="off"
                                                disabled={isSubmitting}
                                            />
                                            <NonAutoFillTextField
                                                name="email"
                                                label="Email"
                                                type="email"
                                                className="auto-complete-off"
                                                autoComplete="off"
                                                disabled={isSubmitting}
                                            />
                                            <SelectField
                                                name="frontendRoleId"
                                                label="Frontend role"
                                                options={frontendRoleOptions}
                                                className=""
                                                disabled={isSubmitting}
                                            />

                                            <div className="flex flex-wrap -mx-2">
                                                <div className="w-full md:w-2/3 px-2 mb-4">
                                                    <TextField
                                                        name="phoneNumber"
                                                        label="Phone number"
                                                        type="text"
                                                        className=""
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="w-full md:w-1/3 px-2 mb-4">
                                                    {" "}
                                                    <IntegerField
                                                        name="bidExpiration"
                                                        label="Bid expiration"
                                                        type="number"
                                                        className=""
                                                        disabled={isSubmitting}
                                                        min={0}
                                                        max={10000}
                                                    />
                                                </div>
                                            </div>

                                            <NonAutoFillTextField
                                                name="password"
                                                label="Password"
                                                type="password"
                                                className=""
                                                autoComplete="off"
                                                disabled={isSubmitting}
                                            />
                                            <NonAutoFillTextField
                                                name="confirmpassword"
                                                label="Confirm password"
                                                type="password"
                                                className=""
                                                autoComplete="off"
                                                disabled={isSubmitting}
                                            />
                                            <TextField
                                                name="updateTimestamp"
                                                label="updateTimestamp"
                                                type="text"
                                                className=""
                                                autoComplete="off"
                                                disabled={true}
                                            />
                                            <TextField
                                                name="updateUserInfo.name"
                                                label="updateUserInfo.name"
                                                type="text"
                                                className=""
                                                autoComplete="off"
                                                disabled={true}
                                            />
                                        </div>
                                        <div className="w-full md:w-1/3 px-2 mb-4">
                                            <MultipleSelectField
                                                name="roleIds"
                                                label="Roles"
                                                options={backendRoleOptions}
                                                className=""
                                                disabled={isSubmitting}
                                                size={backendRoleOptions.length}
                                            />
                                            <TextField
                                                name="company"
                                                label="Company"
                                                type="text"
                                                className=""
                                                autoComplete="off"
                                                disabled={isSubmitting}
                                            />
                                            <SearchMultipleSelectField
                                                name="noteIds"
                                                label="noteIds"
                                                className=""
                                                disabled={isSubmitting}
                                                options={noteOptions}
                                            />
                                            <SelectField
                                                name="clientId"
                                                label="Client (integrator). M2M client id; select INTEGRATOR party or —"
                                                options={integratorOptions}
                                                className=""
                                                disabled={isSubmitting}
                                            />
                                            <CheckBoxField
                                                name="serviceUser"
                                                label="Service user (M2M)"
                                                className=""
                                                disabled={isSubmitting}
                                            />
                                            <CheckBoxField name="restApi" label="REST API access" className="" disabled={isSubmitting} />
                                            <CheckBoxField
                                                name="graphqlApi"
                                                label="GraphQL API access"
                                                className=""
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="w-full md:w-1/3 px-2 mb-4">
                                            <TextAreaField
                                                name="description"
                                                label="Description"
                                                className=""
                                                disabled={isSubmitting}
                                                rows={4}
                                                cols={30}
                                            />
                                            <TextAreaField
                                                name="ipWhitelist"
                                                label="IP whitelist (IPv4 or IPv6, one per line; only used for service users)"
                                                className=""
                                                disabled={isSubmitting}
                                                rows={4}
                                                cols={30}
                                            />
                                        </div>
                                    </div>
                                    {alert.visible ? (
                                        <Alert className="mt-2.5" variant={alert.color} onClose={onDismissAlert} dismissible>
                                            {alert.message}
                                        </Alert>
                                    ) : null}
                                    <SubmitButton disabled={isSubmitting} label={submitButtonLabel} />
                                </Form>
                            </div>
                        </Fragment>
                    )}
                </Formik>
            </div>
            {signedTermsOfService ? (
                <div className="mt-4 mb-1">
                    <Link className="link" to={"/notes/" + signedTermsOfService._id}>
                        Terms of service
                    </Link>
                </div>
            ) : null}

            <div className="mt-4">
                <Grid header="Access" data={table} sortable tableClassName="table-xs">
                    {keys.map((field, i) => (
                        <Column
                            className="nowrap right"
                            key={i}
                            field={field}
                            title={field
                                .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
                                .replace(/^./, (c) => c.toUpperCase()) // Capitalize the first letter
                                .replace(/ (\w)/g, (c) => c.toLowerCase())}
                        />
                    ))}
                </Grid>
            </div>
            <div className="mt-4">
                <Grid header="Logins" data={logins} sortable tableClassName="table-xs">
                    <Column className="nowrap" field="timeStamp" title="timeStamp"></Column>
                    <Column className="nowrap" field="userAgent" title="userAgent"></Column>
                </Grid>
            </div>
        </div>
    );
}
